/**
 * ThemeContext — Chargement dynamique du thème depuis l'API
 *
 * Au montage, récupère GET /api/settings et injecte les couleurs
 * en tant que CSS custom properties sur :root.
 * Fournit le hook useTheme() pour accéder aux paramètres.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { apiFetch } from '../utils/api.js';
import { DEFAULT_SETTINGS, type SiteSettings } from '../types/settings.js';

interface ThemeContextValue {
  settings: SiteSettings | null;
  isLoading: boolean;
  error: string | null;
  /** Recharge les paramètres depuis l'API */
  refetch: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Mappe les champs SiteSettings vers les noms de CSS custom properties */
const COLOR_MAP: Record<string, string> = {
  colorPrimary: '--color-primary',
  colorPrimaryLight: '--color-primary-light',
  colorPrimaryPale: '--color-primary-pale',
  colorBackground: '--color-bg',
  colorSurface: '--color-surface',
  colorText: '--color-text',
  colorTextLight: '--color-text-light',
  colorAccent1: '--color-accent',
  colorAccent2: '--color-accent-light',
};

const FONT_MAP: Record<string, string> = {
  fontPrimary: '--font-primary',
  fontHeading: '--font-heading',
  fontMono: '--font-mono',
};

/** Injecte les couleurs et polices sur :root */
function applyTheme(settings: SiteSettings): void {
  const root = document.documentElement;

  for (const [field, cssVar] of Object.entries(COLOR_MAP)) {
    const value = settings[field as keyof SiteSettings];
    if (typeof value === 'string') {
      root.style.setProperty(cssVar, value);
    }
  }

  for (const [field, cssVar] of Object.entries(FONT_MAP)) {
    const value = settings[field as keyof SiteSettings];
    if (typeof value === 'string') {
      root.style.setProperty(cssVar, `'${value}', system-ui, sans-serif`);
    }
  }

  // Injecter le CSS personnalisé
  let customStyleEl = document.getElementById('arketyp-custom-css');
  if (settings.customCss) {
    if (!customStyleEl) {
      customStyleEl = document.createElement('style');
      customStyleEl.id = 'arketyp-custom-css';
      document.head.appendChild(customStyleEl);
    }
    customStyleEl.textContent = settings.customCss;
  } else if (customStyleEl) {
    customStyleEl.remove();
  }

  // Mettre à jour le titre
  if (settings.siteName) {
    document.title = `${settings.siteName} — ${settings.siteTagline || 'Astrologie'}`;
  }

  // Mettre à jour le favicon
  if (settings.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = settings.faviconUrl;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await apiFetch<SiteSettings>('/settings');
      if (res.success && res.data) {
        setSettings(res.data);
        applyTheme(res.data);
        setError(null);
      } else {
        // Appliquer les défauts si l'API ne répond pas
        setError(res.error ?? 'Impossible de charger les paramètres');
      }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <ThemeContext.Provider value={{ settings, isLoading, error, refetch: fetchSettings }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Hook pour accéder au thème courant */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme doit être utilisé dans un ThemeProvider');
  }
  return ctx;
}

/** Retourne les settings avec les valeurs par défaut si non chargées */
export function useSettings(): SiteSettings {
  const { settings } = useTheme();
  if (!settings) {
    return { id: 1, updatedAt: '', ...DEFAULT_SETTINGS };
  }
  return settings;
}
